import { toApiError, type HttpClient } from './http-client';

/** Au-delà, un envoi de la file est tenu pour perdu : la note reste en file, rien ne part. */
const RAW_TIMEOUT_MS = 15_000;

/**
 * Adaptateur `fetch`.
 *
 * Deux responsabilités au-delà du transport :
 *  - `credentials: 'include'`, parce que la session vit dans un cookie httpOnly ;
 *  - renouvellement du jeton sur 401, **en un seul vol** : sans cela, une page qui lance
 *    cinq requêtes en parallèle déclencherait cinq rafraîchissements concurrents, dont
 *    quatre invalideraient le jeton du cinquième (le refresh est à usage unique).
 */
export class FetchHttpClient implements HttpClient {
  private refreshing: Promise<boolean> | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly onSessionLost: () => void = () => {},
  ) {}

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: 'GET' });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', ...jsonBody(body) });
  }

  postForm<T>(path: string, form: FormData): Promise<T> {
    // Aucun en-tête : `fetch` pose lui-même « multipart/form-data; boundary=… ».
    return this.request<T>(path, { method: 'POST', body: form });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', ...jsonBody(body) });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', ...jsonBody(body) });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  /**
   * Transport de la file offline : jamais d'exception, un statut à interpréter.
   *
   * Il renouvelle le jeton sur 401 comme `request` : l'accès ne vit que quinze minutes,
   * une séance deux heures. Sans cela, une note rejouée après une coupure tombait sur un
   * 401, que la file prenait pour un refus définitif — et la note était jetée.
   *
   * Le délai borne l'attente : sur un wifi qui agonise, `fetch` peut rester suspendu de
   * longues minutes, et la file avec lui.
   */
  async raw(
    path: string,
    init: RequestInit,
    retried = false,
  ): Promise<{ status: number; body: unknown }> {
    let response: Response;
    try {
      response = await fetch(this.url(path), {
        ...init,
        credentials: 'include',
        signal: AbortSignal.timeout(RAW_TIMEOUT_MS),
      });
    } catch {
      // Statut 0 = aucune réponse. La file offline le traduit en « hors-ligne ».
      return { status: 0, body: null };
    }

    if (response.status === 401 && !retried && (await this.refreshSession())) {
      return this.raw(path, init, true);
    }
    return { status: response.status, body: await readBody(response).catch(() => null) };
  }

  private async request<T>(path: string, init: RequestInit, retried = false): Promise<T> {
    const response = await fetch(this.url(path), { ...init, credentials: 'include' });

    if (response.status === 401 && !retried && !path.startsWith('/auth/')) {
      if (await this.refreshSession()) {
        return this.request<T>(path, init, true);
      }
      this.onSessionLost();
    }

    const body = await readBody(response);
    if (!response.ok) throw toApiError(response.status, body);
    return body as T;
  }

  private refreshSession(): Promise<boolean> {
    this.refreshing ??= fetch(this.url('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }
}

function jsonBody(body: unknown): RequestInit {
  if (body === undefined) return {};
  return { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Base de l'API. Vide en production : le front tape `/api` en relatif et nginx route —
 * ce qui garde les cookies en même origine et évite tout CORS.
 */
export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  return `${configured ?? ''}/api`;
}
